// AI advisor card — fetches /{entityType}/:id/ai for live advice and
// /{entityType}/:id/ai-prompt for clipboard copy.
// Shows regime badge, confidence bar, TP/SL suggestions, reasoning, risks.
import { useEffect, useState } from "react";
import { API_BASE } from "@/lib/api-client";
import Badge from "@/components/common/badge";
import {
  type AiAdvice,
  REGIME_LABEL,
  REGIME_COLOR,
  confColor,
  getMainAction,
  getConf,
  getRegime,
  getTpSl,
  parseAdvice,
} from "./ai-advice-card-types";

// ---- Helpers ---------------------------------------------------------------

/** "2m", "1h 12m", "3d" — returns null for <15s (caller shows "just now"). */
function formatAgo(ms: number): string | null {
  if (ms < 15_000) return null;
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const rm = m % 60;
  if (h < 24) return rm > 0 ? `${h}h ${rm}m` : `${h}h`;
  const d = Math.floor(h / 24);
  const rh = h % 24;
  return rh > 0 ? `${d}d ${rh}h` : `${d}d`;
}

// ---- Props -----------------------------------------------------------------

export interface AiAdviceCardProps {
  entityType: "wind" | "grid";
  entityId: string;
  /** Whether AI is enabled for this entity (session.ai_enabled or grid.ai_enabled) */
  aiEnabled: boolean;
  /** JSON string of last stored advice (from cycle.ai_advice or grid.ai_advice) */
  lastAdvice?: string | null;
  /** Called after a successful Ask AI fetch so parent can refetch detail data */
  onRefresh?: () => void;
}

/** Parse the persisted advice JSON and pull its produced-at timestamp.
 *  Backend writes `at` for grid + wind ask-ai routes and `triggered_at` for
 *  wind runtime evaluator. Returns a ms epoch or null when unknown. */
function extractAdviceTime(json: string | null | undefined): number | null {
  if (!json) return null;
  try {
    const p = JSON.parse(json) as { at?: string; triggered_at?: string };
    const iso = p.at ?? p.triggered_at;
    if (!iso) return null;
    const t = new Date(iso).getTime();
    return isNaN(t) ? null : t;
  } catch { return null; }
}

// ---- Component -------------------------------------------------------------

export default function AiAdviceCard({
  entityType,
  entityId,
  aiEnabled,
  lastAdvice,
  onRefresh,
}: AiAdviceCardProps) {
  const [advice, setAdvice] = useState<AiAdvice | null>(() => parseAdvice(lastAdvice));
  // Timestamp tracks when the currently-shown advice was produced. Seeded
  // from the persisted `at` / `triggered_at` field on lastAdvice so the
  // "Xm ago" label is accurate even for advice loaded from the server
  // cache. On a successful manual Ask AI we overwrite with Date.now().
  const [adviceTime, setAdviceTime] = useState<number | null>(
    extractAdviceTime(lastAdvice),
  );
  const [userFetchedAt, setUserFetchedAt] = useState<number>(0);

  // Sync server-cached advice when it changes after mount. Parent polls
  // /grids/:id or /wind/:id/detail every few seconds — grid.ai_advice /
  // cycle.ai_advice mutates when another caller (watcher's auto-AI,
  // background runtime evaluator, or another tab) persists a fresh advice.
  // Without this effect the card only reads lastAdvice at first render,
  // showing a stale snapshot until remount.
  //
  // Do NOT overwrite if the user has performed a manual Ask AI in the last
  // 60s — their fresh result should win over a racing server refetch that
  // might be showing yesterday's cached advice from DB.
  useEffect(() => {
    if (!lastAdvice) return;
    if (userFetchedAt && Date.now() - userFetchedAt < 60_000) return;
    const parsed = parseAdvice(lastAdvice);
    if (!parsed) return;
    setAdvice(parsed);
    setAdviceTime(extractAdviceTime(lastAdvice));
  }, [lastAdvice, userFetchedAt]);
  const [, forceRerender] = useState(0);
  const [loading, setLoading] = useState(false);
  const [copyLoading, setCopyLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  // Rerender every 30s so "Xm ago" stays fresh without parent refetch.
  useEffect(() => {
    if (!adviceTime) return;
    const id = setInterval(() => forceRerender(n => n + 1), 30_000);
    return () => clearInterval(id);
  }, [adviceTime]);

  // Correct API paths: wind uses /wind/:id, grid uses /grids/:id
  const basePath = entityType === "grid" ? `/grids/${entityId}` : `/wind/${entityId}`;
  const disabledTitle = "Enable AI in session/grid settings";

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  }

  async function handleAskAi() {
    if (!aiEnabled || loading) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}${basePath}/ai`, { credentials: "include" });
      if (res.status === 503) {
        setError("AI not configured on server");
        return;
      }
      if (res.status === 400) {
        const body = await res.json() as { error?: string };
        setError(body.error ?? "AI is disabled for this entity");
        return;
      }
      if (!res.ok) {
        const body = await res.json() as { error?: string };
        throw new Error(body.error ?? res.statusText);
      }
      const data = await res.json() as AiAdvice;
      setAdvice(data);
      const now = Date.now();
      setAdviceTime(now);
      setUserFetchedAt(now);
      onRefresh?.();
    } catch (e) {
      setError((e as Error).message ?? "Failed to fetch AI advice");
    } finally {
      setLoading(false);
    }
  }

  async function handleCopyPrompt() {
    if (!aiEnabled || copyLoading) return;
    setCopyLoading(true);
    try {
      const res = await fetch(`${API_BASE}${basePath}/ai-prompt`, { credentials: "include" });
      if (!res.ok) throw new Error(res.statusText);
      const data = await res.json() as { system: string; user: string };
      await navigator.clipboard.writeText(`${data.system}\n\n---USER---\n\n${data.user}`);
      showToast("Prompt copied to clipboard");
    } catch (e) {
      showToast((e as Error).message ?? "Copy failed");
    } finally {
      setCopyLoading(false);
    }
  }

  const regime = advice ? getRegime(advice) : null;
  const conf = advice ? getConf(advice) : null;
  const { tp, sl } = advice ? getTpSl(advice) : { tp: null, sl: null };
  const ago = adviceTime ? formatAgo(Date.now() - adviceTime) : null;

  return (
    <section className="mt-6 mb-6">
      <div className="flex items-baseline justify-between mb-2">
        <div className="text-xs text-muted uppercase tracking-wider font-medium">
          AI Advisor
        </div>
        {advice && (
          <div className="text-[0.7rem] text-muted">
            {adviceTime == null ? "saved earlier" : ago ? `${ago} ago` : "just now"}
          </div>
        )}
      </div>

      <div className="bg-surface border border-border rounded-lg p-3 space-y-2.5">

        {/* Regime badge + main action */}
        {advice && (
          <div className="flex items-center gap-2 flex-wrap">
            {regime && (
              <Badge
                color={REGIME_COLOR[regime]}
                title={`Market regime: ${REGIME_LABEL[regime]}`}
              >
                {regime} · {REGIME_LABEL[regime]}
              </Badge>
            )}
            <span className="text-sm font-semibold text-text">
              {getMainAction(advice)}
            </span>
          </div>
        )}

        {/* Confidence bar */}
        {conf !== null && (
          <div>
            <div className="flex justify-between text-[0.65rem] text-muted mb-0.5">
              <span>Confidence</span>
              <span style={{ color: confColor(conf) }}>{Math.round(conf * 100)}%</span>
            </div>
            <div className="h-1.5 bg-border rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all"
                style={{ width: `${conf * 100}%`, background: confColor(conf) }}
              />
            </div>
          </div>
        )}

        {/* TP / SL suggestions */}
        {(tp != null || sl != null) && (
          <div className="flex gap-3 text-xs">
            {tp != null && <span className="text-success">TP: {tp}</span>}
            {sl != null && <span className="text-danger">SL: {sl}</span>}
          </div>
        )}

        {/* Reasoning */}
        {advice && "why" in advice && (
          <p className="text-[0.78rem] text-dim leading-snug">{advice.why}</p>
        )}

        {/* Risks list */}
        {advice && "risks" in advice && advice.risks.length > 0 && (
          <ul className="space-y-0.5">
            {advice.risks.map((r, i) => (
              <li key={i} className="text-[0.72rem] text-warn flex gap-1.5">
                <span className="opacity-70">&#9888;</span>{r}
              </li>
            ))}
          </ul>
        )}

        {/* Empty state */}
        {!advice && !error && (
          <p className="text-xs text-muted">
            No AI advice yet. Click "Ask AI" to get a recommendation.
          </p>
        )}

        {/* Error state */}
        {error && (
          <p className="text-xs text-danger bg-danger/10 rounded px-2 py-1.5">{error}</p>
        )}

        {/* Toast */}
        {toast && (
          <p className="text-xs text-success bg-success/10 rounded px-2 py-1.5">{toast}</p>
        )}

        {/* Action buttons — clear separation + visible borders on both */}
        <div className="flex flex-wrap gap-3 pt-1">
          <button
            className="text-xs px-3.5 py-1.5 rounded-md border border-accent/60 bg-accent/10 text-accent font-medium hover:bg-accent/20 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-accent/10"
            disabled={!aiEnabled || loading}
            onClick={handleAskAi}
            title={!aiEnabled ? disabledTitle : undefined}
          >
            {loading ? "Asking…" : "Ask AI"}
          </button>
          <button
            className="text-xs px-3.5 py-1.5 rounded-md border border-dim/40 bg-bg/40 text-dim hover:text-text hover:border-dim/60 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            disabled={!aiEnabled || copyLoading}
            onClick={handleCopyPrompt}
            title={!aiEnabled ? disabledTitle : undefined}
          >
            {copyLoading ? "Copying…" : "Copy Prompt"}
          </button>
        </div>

      </div>
    </section>
  );
}
